import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Switch,
  Paper
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
// Using the same API base URL pattern as other components
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

interface CreateUserData {
  fullName: string;
  email: string;
  phone: string;
  temporaryPassword: string;
  addToGuestList: boolean;
  
  // Enhanced PIN-first authentication fields
  authMethod: 'pin' | 'password' | 'both';
  generatePin: boolean;
  customPin: string;
  emailPin: boolean;
}

interface CreateUserResponse {
  success: boolean;
  message: string;
  data?: {
    email: string;
    fullName: string;
    temporaryPassword: string;
    pin?: string;
    cognitoUserCreated: boolean;
    guestListAdded: boolean;
    emailSent: boolean;
    note: string;
  };
  processingResults?: {
    cognitoUserCreated: boolean;
    guestListAdded: boolean;
    emailSent: boolean;
    errors: string[];
  };
}

interface PasswordValidation {
  isValid: boolean;
  requirements: {
    minLength: boolean;
    hasUppercase: boolean;
    hasLowercase: boolean;
    hasNumber: boolean;
    hasSpecialChar: boolean;
  };
  errors: string[];
}

interface AdminUserCreatorProps {
  onUserCreated?: () => void;
}

export const AdminUserCreator: React.FC<AdminUserCreatorProps> = ({ onUserCreated }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<CreateUserData>({
    fullName: '',
    email: '',
    phone: '',
    temporaryPassword: '',
    addToGuestList: true,
    
    // PIN-first defaults
    authMethod: 'pin',
    generatePin: true,
    customPin: '',
    emailPin: true
  });
  const [result, setResult] = useState<CreateUserResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>({
    isValid: false,
    requirements: {
      minLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSpecialChar: false
    },
    errors: []
  });
  const [copySuccess, setCopySuccess] = useState(false);
  const [pinValidation, setPinValidation] = useState({
    isValid: false,
    error: ''
  });

  const validatePin = (pin: string) => {
    if (!pin) {
      return { isValid: true, error: '' }; // Empty is valid when auto-generating
    }
    
    if (pin.length !== 4) {
      return { isValid: false, error: 'PIN must be exactly 4 digits' };
    }
    
    if (!/^\d{4}$/.test(pin)) {
      return { isValid: false, error: 'PIN must contain only numbers' };
    }
    
    // Check for sequential numbers
    const isSequential = pin === '1234' || pin === '4321' || pin === '0123' || pin === '3210';
    if (isSequential) {
      return { isValid: false, error: 'PIN should not be sequential numbers' };
    }
    
    // Check for repeated digits
    const isRepeated = /^(\d)\1{3}$/.test(pin);
    if (isRepeated) {
      return { isValid: false, error: 'PIN should not be all the same digit' };
    }
    
    return { isValid: true, error: '' };
  };

  const validatePassword = (password: string): PasswordValidation => {
    const requirements = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
    
    const errors = [];
    if (!requirements.minLength) errors.push('At least 8 characters');
    if (!requirements.hasUppercase) errors.push('One uppercase letter');
    if (!requirements.hasLowercase) errors.push('One lowercase letter');
    if (!requirements.hasNumber) errors.push('One number');
    if (!requirements.hasSpecialChar) errors.push('One special character');
    
    return {
      isValid: Object.values(requirements).every(req => req),
      requirements,
      errors
    };
  };

  const generatePin = () => {
    let pin: string;
    let attempts = 0;
    const maxAttempts = 100;
    
    do {
      pin = Math.floor(1000 + Math.random() * 9000).toString();
      attempts++;
    } while (!validatePin(pin).isValid && attempts < maxAttempts);
    
    setFormData(prev => ({ ...prev, customPin: pin }));
  };

  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one of each required character type
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // Uppercase
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // Lowercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // Number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // Special char
    
    // Fill the rest randomly
    for (let i = 4; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    const shuffled = password.split('').sort(() => Math.random() - 0.5).join('');
    setFormData(prev => ({ ...prev, temporaryPassword: shuffled }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Validate password whenever it changes
  useEffect(() => {
    if (formData.temporaryPassword) {
      setPasswordValidation(validatePassword(formData.temporaryPassword));
    } else {
      setPasswordValidation({
        isValid: false,
        requirements: {
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecialChar: false
        },
        errors: []
      });
    }
  }, [formData.temporaryPassword]);

  // Validate PIN whenever it changes
  useEffect(() => {
    if (formData.customPin || !formData.generatePin) {
      setPinValidation(validatePin(formData.customPin));
    } else {
      setPinValidation({ isValid: true, error: '' });
    }
  }, [formData.customPin, formData.generatePin]);

  const handleInputChange = (field: keyof CreateUserData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    
    // Show immediate feedback
    console.log('Creating guest account...');

    try {
      // Prepare the payload with enhanced PIN-first fields
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        addToGuestList: formData.addToGuestList,
        
        // Enhanced authentication fields
        authMethod: formData.authMethod,
        generatePin: formData.generatePin,
        emailPin: formData.emailPin,
        
        // Optional fields
        ...(formData.phone.trim() && { phone: formData.phone.trim() }),
        ...(formData.customPin && !formData.generatePin && { customPin: formData.customPin }),
        ...((formData.authMethod === 'password' || formData.authMethod === 'both') && { 
          temporaryPassword: formData.temporaryPassword 
        })
      };

      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/users/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });

      // Check if response is ok first
      if (!response.ok) {
        const errorText = await response.text();
        setError(`HTTP ${response.status}: ${response.statusText}. ${errorText}`);
        console.error('HTTP Error:', response.status, response.statusText, errorText);
        return;
      }

      const data: CreateUserResponse = await response.json();
      
      if (data.success) {
        setResult(data);
        // Reset form on success
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          temporaryPassword: '',
          addToGuestList: true,
          authMethod: 'pin',
          generatePin: true,
          customPin: '',
          emailPin: true
        });
        // Notify parent component that a user was created
        if (onUserCreated && data.data?.guestListAdded) {
          onUserCreated();
        }
      } else {
        // Show more detailed error information
        const errorMessage = data.message || 'Failed to create user';
        const details = (data as any).details || '';
        setError(details ? `${errorMessage}: ${details}` : errorMessage);
        console.error('API Error:', data);
      }
    } catch (err) {
      setError(`Network error occurred while creating user: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error creating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError('');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      temporaryPassword: '',
      addToGuestList: true,
      authMethod: 'pin',
      generatePin: true,
      customPin: '',
      emailPin: true
    });
  };

  const isFormValid = formData.fullName.trim() && 
                     formData.email.trim() && 
                     (formData.authMethod === 'pin' ? 
                       (formData.generatePin || pinValidation.isValid) :
                       formData.temporaryPassword.trim() && passwordValidation.isValid
                     );

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={() => setOpen(true)}
        sx={{ mb: 2 }}
      >
        Create Guest with PIN
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonAddIcon />
            Create Guest with PIN
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {result && (
            <Card sx={{ mb: 2, bgcolor: result.data?.cognitoUserCreated ? 'success.light' : 'error.light' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color={result.data?.cognitoUserCreated ? 'success.main' : 'error.main'}>
                  {result.data?.cognitoUserCreated ? '✅ User Created Successfully!' : '❌ User Creation Failed'}
                </Typography>
                {result.data?.cognitoUserCreated && result.processingResults?.errors && result.processingResults.errors.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      User was created successfully, but some optional steps had issues:
                    </Typography>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {result.processingResults.errors.map((error, index) => (
                        <li key={index} style={{ fontSize: '0.875rem' }}>
                          {error.includes('Welcome email failed') ? 'Welcome email could not be sent (email not verified in SES)' : error}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {!result.data?.cognitoUserCreated && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {result.message}
                  </Typography>
                )}
                
                {result.data && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">User Details:</Typography>
                      <Typography variant="body2">Name: {result.data.fullName}</Typography>
                      <Typography variant="body2">Email: {result.data.email}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'background.paper', p: 1, borderRadius: 1, flexGrow: 1 }}>
                          Temp Password: {result.data.temporaryPassword}
                        </Typography>
                        <Tooltip title={copySuccess ? 'Copied!' : 'Copy password'}>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(result.data!.temporaryPassword)}
                            color={copySuccess ? 'success' : 'default'}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {result.data.pin && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'success.light', color: 'success.contrastText', p: 1, borderRadius: 1, flexGrow: 1, fontWeight: 'bold' }}>
                            🔐 PIN: {result.data.pin}
                          </Typography>
                          <Tooltip title={copySuccess ? 'Copied!' : 'Copy PIN'}>
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(result.data!.pin!)}
                              color={copySuccess ? 'success' : 'default'}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">Status:</Typography>
                      <Typography variant="body2" color={result.data.cognitoUserCreated ? 'success.main' : 'error.main'}>
                        Cognito User: {result.data.cognitoUserCreated ? '✓ Created' : '✗ Failed'}
                      </Typography>
                      <Typography variant="body2" color={result.data.guestListAdded ? 'success.main' : 'warning.main'}>
                        Guest List: {result.data.guestListAdded ? '✓ Added' : '- Skipped'}
                      </Typography>
                      <Typography variant="body2" color={result.data.emailSent ? 'success.main' : 'warning.main'}>
                        Welcome Email: {result.data.emailSent ? '✓ Sent' : '⚠ Failed/Skipped'}
                      </Typography>
                      {!result.data.emailSent && result.data.cognitoUserCreated && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          💡 Share the {result.data.pin ? 'PIN' : 'temporary password'} manually with the user
                        </Typography>
                      )}
                      {result.data.pin && result.data.emailSent && (
                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                          🎉 User can now log in with their 4-digit PIN!
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          )}

          {/* Basic Information */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            📝 Basic Information
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number (Optional)"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                placeholder="+1-555-123-4567"
                disabled={loading}
              />
            </Grid>
          </Grid>

          {/* Authentication Method Selection */}
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon /> Authentication Method
          </Typography>
          
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <FormControl component="fieldset">
              <RadioGroup
                value={formData.authMethod}
                onChange={(e) => setFormData(prev => ({ ...prev, authMethod: e.target.value as 'pin' | 'password' | 'both' }))}
                row
              >
                <FormControlLabel 
                  value="pin" 
                  control={<Radio />} 
                  label="PIN Authentication (Recommended)" 
                  disabled={loading}
                />
                <FormControlLabel 
                  value="password" 
                  control={<Radio />} 
                  label="Password Authentication (Legacy)" 
                  disabled={loading}
                />
                <FormControlLabel 
                  value="both" 
                  control={<Radio />} 
                  label="Both PIN and Password" 
                  disabled={loading}
                />
              </RadioGroup>
            </FormControl>
          </Paper>

          {/* PIN Configuration */}
          {(formData.authMethod === 'pin' || formData.authMethod === 'both') && (
            <>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                📱 PIN Configuration
              </Typography>
              
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.generatePin}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            generatePin: e.target.checked,
                            customPin: e.target.checked ? '' : prev.customPin
                          }))}
                          disabled={loading}
                        />
                      }
                      label="Generate PIN automatically"
                    />
                  </Grid>
                  
                  {!formData.generatePin && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" gap={1}>
                        <TextField
                          fullWidth
                          label="Custom PIN"
                          value={formData.customPin}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setFormData(prev => ({ ...prev, customPin: value }));
                          }}
                          placeholder="1234"
                          inputProps={{ maxLength: 4 }}
                          error={!pinValidation.isValid && formData.customPin.length > 0}
                          helperText={!pinValidation.isValid && formData.customPin.length > 0 ? pinValidation.error : 'Enter exactly 4 digits'}
                          disabled={loading}
                        />
                        <Button
                          variant="outlined"
                          onClick={generatePin}
                          disabled={loading}
                          sx={{ minWidth: 'auto', px: 2 }}
                          startIcon={<AutoAwesomeIcon />}
                        >
                          Generate
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.emailPin}
                          onChange={(e) => setFormData(prev => ({ ...prev, emailPin: e.target.checked }))}
                          disabled={loading}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <EmailIcon fontSize="small" />
                          Email PIN to guest
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}

          {/* Password Configuration (for legacy/both modes) */}
          {(formData.authMethod === 'password' || formData.authMethod === 'both') && (
            <>
              <Typography variant="h6" sx={{ mb: 2 }}>
                🔐 Password Configuration
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" gap={1}>
                    <Box sx={{ width: '100%' }}>
                      <TextField
                        fullWidth
                        label="Temporary Password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.temporaryPassword}
                        onChange={handleInputChange('temporaryPassword')}
                        required={formData.authMethod === 'password' || formData.authMethod === 'both'}
                        disabled={loading}
                        error={formData.temporaryPassword.length > 0 && !passwordValidation.isValid}
                        helperText={
                          formData.temporaryPassword.length > 0 && !passwordValidation.isValid
                            ? `Missing: ${passwordValidation.errors.join(', ')}`
                            : ''
                        }
                        InputProps={{
                          endAdornment: (
                            <Button
                              size="small"
                              onClick={() => setShowPassword(!showPassword)}
                              disabled={loading}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </Button>
                          )
                        }}
                      />
                      
                      {formData.temporaryPassword && (
                        <Box sx={{ mt: 1 }}>
                          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Password Strength:
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={(Object.values(passwordValidation.requirements).filter(Boolean).length / 5) * 100}
                              sx={{ 
                                flexGrow: 1, 
                                height: 6, 
                                borderRadius: 3,
                                backgroundColor: 'grey.300',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: passwordValidation.isValid ? 'success.main' : 'warning.main'
                                }
                              }}
                            />
                            <Typography variant="caption" color={passwordValidation.isValid ? 'success.main' : 'warning.main'}>
                              {passwordValidation.isValid ? 'Strong' : 'Weak'}
                            </Typography>
                          </Box>
                          
                          <List dense sx={{ py: 0 }}>
                            {Object.entries(passwordValidation.requirements).map(([key, met]) => {
                              const labels = {
                                minLength: 'At least 8 characters',
                                hasUppercase: 'One uppercase letter (A-Z)',
                                hasLowercase: 'One lowercase letter (a-z)',
                                hasNumber: 'One number (0-9)',
                                hasSpecialChar: 'One special character (!@#$%^&*)'
                              };
                              
                              return (
                                <ListItem key={key} sx={{ py: 0, px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 24 }}>
                                    {met ? (
                                      <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                    ) : (
                                      <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={labels[key as keyof typeof labels]}
                                    primaryTypographyProps={{ 
                                      variant: 'caption',
                                      color: met ? 'success.main' : 'text.secondary'
                                    }}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      onClick={generatePassword}
                      disabled={loading}
                      sx={{ minWidth: 'auto', px: 2, alignSelf: 'flex-start' }}
                    >
                      Generate
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {/* Event Registration */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            🎉 Event Registration
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.addToGuestList}
                    onChange={handleInputChange('addToGuestList')}
                    disabled={loading}
                  />
                }
                label="Add to Guest List"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          
          <Alert severity="info">
            <Typography variant="body2">
              {formData.authMethod === 'pin' 
                ? 'The user will be created with PIN authentication. They can log in immediately using their 4-digit PIN.'
                : formData.authMethod === 'password'
                ? 'The user will be created in Cognito with FORCE_CHANGE_PASSWORD status, meaning they must set a new password on their first login.'
                : 'The user will be created with both PIN and password authentication options available.'
              }
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!isFormValid || loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {loading ? 'Creating...' : 'Create Guest'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};